// =====================================================================
// MeloPainelTab (Painel da Carol / Pablo — Fase B / S72) — gated company 3 no index.
// O painel que a Carol vive. 3 seções:
//   Caixa do dia (faturamento hoje/mês + ticket médio + projeção + as 5 metas) ·
//   Metas (base do mês + faixas nomeadas Prata/Ouro/Diamante/Esmeralda/Terras Raras) ·
//   Relatório por pessoa (o "Desafio": agendamentos R$110 + vendas + comissão por pessoa).
// Backend isolado /finance/melo/* (caixa só LÊ finance_payments/expenses). Auto-contido,
// espelha o padrão do MeloCommissionTab.js.
// =====================================================================
import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Tabs, Tab, Tooltip, Typography, Checkbox, FormControlLabel
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import RefreshIcon from "@material-ui/icons/Refresh";
import { toast } from "react-toastify";
import {
  meloCaixaDoDia, meloRelatorioPessoa, getMetaBaseline, upsertMetaBaseline,
  listMetaTiers, upsertMetaTier, deleteMetaTier, fmtMoney, financeErrorMessage
} from "../../services/financeService";
import useIsMobile from "../../utils/useIsMobile";

const useStyles = makeStyles((theme) => ({
  toolbar: { display: "flex", gap: theme.spacing(1.5), alignItems: "center", flexWrap: "wrap", margin: theme.spacing(1, 0, 2) },
  empty: { padding: theme.spacing(4), textAlign: "center", color: theme.palette.text.secondary },
  loading: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 160 },
  hint: { color: theme.palette.text.secondary, margin: theme.spacing(0.5, 0, 1.5), fontSize: 13 },
  cards: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: theme.spacing(1.5), margin: theme.spacing(1, 0, 2.5) },
  card: { padding: theme.spacing(1.5, 2), border: `1px solid ${theme.palette.divider}`, borderRadius: 10, background: theme.palette.background.paper },
  consolidCard: { display: "inline-block", padding: theme.spacing(1.5, 2.5), border: `1px solid ${theme.palette.divider}`, borderRadius: 10, background: theme.palette.type === "dark" ? "rgba(21,101,192,0.12)" : "#e3f2fd", margin: theme.spacing(0.5, 0, 16 / 8) },
  cardLabel: { fontSize: 12, color: theme.palette.text.secondary, textTransform: "uppercase", letterSpacing: 0.4 },
  cardValue: { fontSize: 24, fontWeight: 700, lineHeight: 1.2, marginTop: 2 },
  cardSub: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 2 },
  sectionTitle: { fontWeight: 700, margin: theme.spacing(2, 0, 1) },
  metaRow: { margin: theme.spacing(1.2, 0) },
  metaHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 13, marginBottom: 4 },
  metaTrack: { position: "relative", height: 14, borderRadius: 7, background: "#eceff1", overflow: "hidden" },
  metaFill: { position: "absolute", left: 0, top: 0, height: "100%", borderRadius: 7, transition: "width .4s ease" },
  metaProj: { position: "absolute", top: -2, width: 0, height: 18, borderLeft: "2px dashed #455a64" },
  spark: { display: "flex", alignItems: "flex-end", gap: 2, height: 46, marginTop: 6, padding: theme.spacing(0, 0.5) },
  sparkBar: { flex: 1, minWidth: 3, background: "#90caf9", borderRadius: "2px 2px 0 0", transition: "height .3s" },
  mutirao: { padding: theme.spacing(1.5, 2), borderRadius: 8, background: "#fff3e0", border: "1px solid #ffb74d", color: "#e65100", margin: theme.spacing(1, 0, 2), fontSize: 14, fontWeight: 600 }
}));

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const num = (v) => (v == null || v === "" ? null : Number(v));
const todayLocal = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD (TZ do navegador = BRT)
const fmtDia = (iso) => { if (!iso) return "—"; const [y, m, d] = String(iso).slice(0, 10).split("-"); return d && m && y ? `${d}/${m}` : iso; };

const MeloPainelTab = ({ unitId }) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  // [S90 F5] unidade ativa (do seletor). 'all'/undefined = Caixa Geral (consolidado:
  // caixa/metas = soma de todas as unidades; a BASE não se edita aqui, é a soma).
  const isConsolidado = unitId == null || String(unitId) === "all";
  const [sub, setSub] = useState(0);
  const [loading, setLoading] = useState(false);
  // caixa
  const [dia, setDia] = useState(todayLocal());
  const [dash, setDash] = useState(null);
  // metas
  const now = new Date();
  const [periodo, setPeriodo] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [tiers, setTiers] = useState([]);
  const [baseline, setBaseline] = useState({ baseAmount: 0, note: "" });
  const [baseDraft, setBaseDraft] = useState("");
  // relatório por pessoa
  const [relFilters, setRelFilters] = useState({ from: "", to: "" });
  const [rel, setRel] = useState(null);
  // dialog de meta (faixa nomeada)
  const [dlg, setDlg] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchCaixa = useCallback(async () => {
    setLoading(true);
    try { setDash(await meloCaixaDoDia(dia || undefined, unitId)); }
    catch (e) { toast.error("Falha ao carregar o caixa."); }
    finally { setLoading(false); }
  }, [dia, unitId]);

  const fetchMetas = useCallback(async () => {
    setLoading(true);
    try {
      const [t, b] = await Promise.all([listMetaTiers(), getMetaBaseline(periodo.year, periodo.month, unitId)]);
      setTiers(t?.tiers || []);
      const base = b?.baseline || { baseAmount: 0, note: "" };
      setBaseline(base);
      setBaseDraft(base.baseAmount ? String(base.baseAmount) : "");
    } catch (e) { toast.error("Falha ao carregar as metas."); }
    finally { setLoading(false); }
  }, [periodo, unitId]);

  const fetchRel = useCallback(async () => {
    setLoading(true);
    try { setRel(await meloRelatorioPessoa({ from: relFilters.from || undefined, to: relFilters.to || undefined })); }
    catch (e) { toast.error("Falha ao carregar o relatório por pessoa."); }
    finally { setLoading(false); }
  }, [relFilters]);

  useEffect(() => { if (sub === 0) fetchCaixa(); }, [sub, fetchCaixa]);
  useEffect(() => { if (sub === 1) fetchMetas(); }, [sub, fetchMetas]);
  useEffect(() => { if (sub === 2) fetchRel(); }, [sub, fetchRel]);

  // ---- metas: salvar base do mês ----
  const saveBase = async () => {
    if (isConsolidado) {
      toast.error("Selecione uma unidade específica para definir a base (o Caixa Geral é a soma de todas).");
      return;
    }
    setSaving(true);
    try {
      await upsertMetaBaseline({ year: periodo.year, month: periodo.month, baseAmount: num(baseDraft) || 0, note: baseline.note || null, unitId });
      toast.success("Base do mês salva.");
      fetchMetas();
    } catch (e) { toast.error(financeErrorMessage(e)); }
    finally { setSaving(false); }
  };
  // ---- metas: salvar faixa nomeada ----
  const saveTier = async () => {
    const { data } = dlg;
    setSaving(true);
    try {
      await upsertMetaTier({ id: data.id, name: data.name, pct: num(data.pct) || 0, gemColor: data.gemColor || null, sortOrder: num(data.sortOrder) || 0, active: data.active !== false });
      toast.success("Meta salva.");
      setDlg(null);
      fetchMetas();
    } catch (e) { toast.error(financeErrorMessage(e)); }
    finally { setSaving(false); }
  };
  const delTier = async (t) => {
    if (!window.confirm(`Excluir a meta "${t.name}"?`)) return;
    try { await deleteMetaTier(t.id); fetchMetas(); }
    catch (e) { toast.error(financeErrorMessage(e)); }
  };
  const d = (patch) => setDlg((p) => ({ ...p, data: { ...p.data, ...patch } }));

  const sparkMax = dash && dash.serieDiaria && dash.serieDiaria.length ? Math.max(...dash.serieDiaria.map((s) => s.entradas), 1) : 1;

  return (
    <>
      <Tabs value={sub} onChange={(e, v) => setSub(v)} indicatorColor="primary" textColor="primary" variant="scrollable" style={{ marginBottom: 8 }}>
        <Tab label="Caixa do dia" value={0} />
        <Tab label="Metas" value={1} />
        <Tab label="Relatório por pessoa" value={2} />
      </Tabs>

      {/* ========================= CAIXA DO DIA ========================= */}
      {sub === 0 && (
        <>
          <div className={classes.toolbar}>
            <TextField type="date" size="small" variant="outlined" label="Dia" value={dia}
              onChange={(e) => setDia(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button size="small" startIcon={<RefreshIcon />} onClick={fetchCaixa}>Atualizar</Button>
            <Box flexGrow={1} />
            {dash && <Typography variant="caption" color="textSecondary">{MESES[(dash.mesNumero || 1) - 1]} / {dash.ano} · {dash.mes ? `${dash.mes.diasComMovimento} dia(s) com caixa` : ""}</Typography>}
          </div>

          {loading || !dash ? <div className={classes.loading}><CircularProgress /></div> : (
            <>
              {dash.hoje.faturamento === 0 && (
                <div className={classes.mutirao}>⚠️ Caixa de {fmtDia(dash.dia)} ainda zerado — hora do mutirão (resgate / confirmações) pra não deixar o dia fraco.</div>
              )}

              <div className={classes.cards}>
                <div className={classes.card}>
                  <div className={classes.cardLabel}>Faturamento hoje</div>
                  <div className={classes.cardValue} style={{ color: "#2e7d32" }}>{fmtMoney(dash.hoje.faturamento)}</div>
                  <div className={classes.cardSub}>{dash.hoje.recebimentos} recebimento(s) · {dash.hoje.pacientes} paciente(s)</div>
                </div>
                <div className={classes.card}>
                  <div className={classes.cardLabel}>Faturamento do mês</div>
                  <div className={classes.cardValue}>{fmtMoney(dash.mes.faturamento)}</div>
                  <div className={classes.cardSub}>saldo {fmtMoney(dash.mes.saldo)} (− saídas {fmtMoney(dash.mes.saidas)})</div>
                </div>
                <div className={classes.card}>
                  <div className={classes.cardLabel}>Ticket médio (mês)</div>
                  <div className={classes.cardValue}>{fmtMoney(dash.mes.ticketMedio)}</div>
                  <div className={classes.cardSub}>{dash.mes.pacientes} paciente(s) no mês</div>
                </div>
                <div className={classes.card}>
                  <div className={classes.cardLabel}>Projeção do mês</div>
                  <div className={classes.cardValue} style={{ color: "#1565c0" }}>{fmtMoney(dash.mes.projecao)}</div>
                  <div className={classes.cardSub}>dia {dash.mes.diasDecorridos}/{dash.mes.diasNoMes}</div>
                </div>
              </div>

              {/* mini-gráfico diário */}
              {dash.serieDiaria && dash.serieDiaria.length > 0 && (
                <>
                  <Typography variant="caption" color="textSecondary">Faturamento por dia (mês)</Typography>
                  <div className={classes.spark}>
                    {dash.serieDiaria.map((s) => (
                      <Tooltip key={s.dia} title={`${fmtDia(s.dia)} · ${fmtMoney(s.entradas)}`}>
                        <div className={classes.sparkBar} style={{ height: `${Math.max(3, (s.entradas / sparkMax) * 100)}%` }} />
                      </Tooltip>
                    ))}
                  </div>
                </>
              )}

              {/* metas */}
              <Typography className={classes.sectionTitle}>🎯 Metas do mês {dash.metas.configurada ? "" : "(defina a base na aba Metas)"}</Typography>
              {!dash.metas.configurada ? (
                <div className={classes.hint}>A base do mês ainda não foi definida — vá em <b>Metas</b> e informe a base sobre a qual incidem os percentuais.</div>
              ) : (
                <>
                  <div className={classes.hint}>Base do mês {fmtMoney(dash.metas.base)} · faturamento atual {fmtMoney(dash.mes.faturamento)} · projeção {fmtMoney(dash.mes.projecao)}{dash.metas.nivelAtual ? ` · nível atual: ${dash.metas.nivelAtual}` : ""}</div>
                  {dash.metas.niveis.map((n) => (
                    <div key={n.name} className={classes.metaRow}>
                      <div className={classes.metaHead}>
                        <span><b>{n.name}</b> <span style={{ color: "#90a4ae" }}>+{n.pct}% · meta {fmtMoney(n.target)}</span></span>
                        <span>{n.batidaMes ? <Chip size="small" label="batida ✓" style={{ backgroundColor: n.gemColor || "#2e7d32", color: "#fff", height: 20 }} /> : <span style={{ color: "#607d8b" }}>{n.atingidoPct}%</span>}</span>
                      </div>
                      <div className={classes.metaTrack}>
                        <div className={classes.metaFill} style={{ width: `${Math.min(100, n.atingidoPct)}%`, background: n.gemColor || "#1565c0" }} />
                        {n.projetadoPct > 0 && n.projetadoPct <= 100 && !n.batidaMes && (
                          <Tooltip title={`projeção ${n.projetadoPct}%`}><div className={classes.metaProj} style={{ left: `${Math.min(100, n.projetadoPct)}%` }} /></Tooltip>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className={classes.hint} style={{ marginTop: 10 }}><span style={{ borderLeft: "2px dashed #455a64", paddingLeft: 6 }}>linha tracejada = onde a projeção do mês chega.</span></div>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* ============================= METAS ============================= */}
      {sub === 1 && (
        <>
          <div className={classes.toolbar}>
            <TextField select size="small" variant="outlined" label="Mês" value={periodo.month} onChange={(e) => setPeriodo((p) => ({ ...p, month: Number(e.target.value) }))} style={{ flexGrow: 1, minWidth: 130 }}>
              {MESES.map((m, i) => <MenuItem key={i} value={i + 1}>{m}</MenuItem>)}
            </TextField>
            <TextField type="number" size="small" variant="outlined" label="Ano" value={periodo.year} onChange={(e) => setPeriodo((p) => ({ ...p, year: Number(e.target.value) }))} style={{ flexGrow: 1, minWidth: 130 }} />
            <Box flexGrow={1} />
            <Button size="small" variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setDlg({ type: "tier", data: { active: true, sortOrder: (tiers.length + 1) * 1, gemColor: "#a855f7" } })}>+ pedra nova</Button>
          </div>

          {loading ? <div className={classes.loading}><CircularProgress /></div> : (
            <>
              {isConsolidado ? (
                <>
                  <div className={classes.hint}>👁️ <b>Caixa Geral (consolidado):</b> a base abaixo é a <b>soma</b> das 4 unidades. Para <b>definir</b> a base de cada clínica, selecione a unidade (Moema/Itajaí/JP/Brasília) no seletor de cima.</div>
                  <div className={classes.consolidCard}>
                    <div className={classes.cardLabel}>Base consolidada · {MESES[periodo.month - 1]}/{periodo.year} (soma das unidades)</div>
                    <div className={classes.cardValue}>{fmtMoney(baseline.baseAmount)}</div>
                  </div>
                </>
              ) : (
                <>
                  <div className={classes.hint}>A Carol digita a <b>base do mês</b> desta unidade — cada meta é a base + o percentual dela. Ex.: base R$339.512 → Ouro (+20%) = meta R$407.414.</div>
                  <Box className={classes.toolbar} style={{ margin: "4px 0 16px" }}>
                    <TextField type="number" size="small" variant="outlined"
                      label={`Base de ${MESES[periodo.month - 1]}/${periodo.year} (R$)`}
                      value={baseDraft} onChange={(e) => setBaseDraft(e.target.value)} style={{ flexGrow: 1, minWidth: 130 }} />
                    <Button variant="contained" color="primary" onClick={saveBase} disabled={saving}>Salvar base</Button>
                  </Box>
                </>
              )}

              <Typography variant="subtitle2" gutterBottom>Metas (faixas nomeadas)</Typography>
              <div style={{ overflowX: "auto", width: "100%" }}>
              <Table size="small" style={{ minWidth: 640 }}>
                <TableHead><TableRow><TableCell>Meta</TableCell><TableCell align="right">%</TableCell><TableCell align="right">Alvo no mês</TableCell><TableCell align="center">Cor</TableCell><TableCell align="center">Ativa</TableCell><TableCell align="center" style={{ width: 90 }}>Ações</TableCell></TableRow></TableHead>
                <TableBody>
                  {tiers.length === 0 ? <TableRow><TableCell colSpan={6} className={classes.empty}>Nenhuma meta. Crie as pedras (Prata/Ouro/Diamante/Esmeralda/Terras Raras).</TableCell></TableRow>
                    : tiers.map((t) => {
                      const base = num(baseDraft) || baseline.baseAmount || 0;
                      const alvo = base * (1 + Number(t.pct) / 100);
                      return (
                        <TableRow key={t.id} hover>
                          <TableCell><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: t.gemColor || "#bbb", marginRight: 8 }} /><b>{t.name}</b></TableCell>
                          <TableCell align="right">+{Number(t.pct)}%</TableCell>
                          <TableCell align="right">{base > 0 ? fmtMoney(alvo) : "—"}</TableCell>
                          <TableCell align="center"><span style={{ display: "inline-block", width: 16, height: 16, borderRadius: 4, background: t.gemColor || "#bbb", border: "1px solid #ccc" }} /></TableCell>
                          <TableCell align="center">{t.active ? "Sim" : "Não"}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Editar"><IconButton size="small" onClick={() => setDlg({ type: "tier", data: { ...t } })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Excluir"><IconButton size="small" onClick={() => delTier(t)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
              </div>
            </>
          )}
        </>
      )}

      {/* ==================== RELATÓRIO POR PESSOA (Desafio) ==================== */}
      {sub === 2 && (
        <>
          <div className={classes.toolbar}>
            <TextField type="date" size="small" variant="outlined" label="De" value={relFilters.from} onChange={(e) => setRelFilters((f) => ({ ...f, from: e.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField type="date" size="small" variant="outlined" label="Até" value={relFilters.to} onChange={(e) => setRelFilters((f) => ({ ...f, to: e.target.value }))} InputLabelProps={{ shrink: true }} />
            <Button size="small" startIcon={<RefreshIcon />} onClick={fetchRel}>Atualizar</Button>
            <Box flexGrow={1} />
            <Typography variant="caption" color="textSecondary">{relFilters.from || relFilters.to ? "período selecionado" : "mês atual"}</Typography>
          </div>
          <div className={classes.hint}>O <b>Desafio</b>: por pessoa, os agendamentos (R$110), as vendas e a comissão. <i>Atendimentos/conversa por WhatsApp entram quando o número oficial do Pablo subir.</i></div>

          {loading || !rel ? <div className={classes.loading}><CircularProgress /></div> : (
            <>
              <Typography variant="subtitle2" gutterBottom>🏆 Equipe (recepção / comercial)</Typography>
              {rel.equipe.length === 0 ? <div className={classes.empty}><Typography>Sem comissões de equipe no período. (As comissões nascem das baixas de pagamento — apure na aba Comissão.)</Typography></div> : (
                <div style={{ overflowX: "auto", width: "100%" }}>
                <Table size="small" style={{ marginBottom: 18, minWidth: 640 }}>
                  <TableHead><TableRow><TableCell>Pessoa</TableCell><TableCell align="right">Agendamentos</TableCell><TableCell align="right">Com. agend.</TableCell><TableCell align="right">Vendas</TableCell><TableCell align="right">Valor vendas</TableCell><TableCell align="right">Com. venda</TableCell><TableCell align="right">Total</TableCell></TableRow></TableHead>
                  <TableBody>
                    {rel.equipe.map((p) => (
                      <TableRow key={p.userId} hover>
                        <TableCell>{p.nome}</TableCell>
                        <TableCell align="right">{p.agendamentos}</TableCell>
                        <TableCell align="right">{fmtMoney(p.comissaoAgendamento)}</TableCell>
                        <TableCell align="right">{p.vendas}</TableCell>
                        <TableCell align="right">{fmtMoney(p.valorVendas)}</TableCell>
                        <TableCell align="right">{fmtMoney(p.comissaoVenda)}</TableCell>
                        <TableCell align="right" style={{ fontWeight: 700, color: "#1565c0" }}>{fmtMoney(p.comissaoTotal)}</TableCell>
                      </TableRow>
                    ))}
                    {rel.totais && (
                      <TableRow>
                        <TableCell style={{ fontWeight: 700 }}>Total</TableCell>
                        <TableCell align="right" style={{ fontWeight: 700 }}>{rel.totais.agendamentos}</TableCell>
                        <TableCell align="right" style={{ fontWeight: 700 }}>{fmtMoney(rel.totais.comissaoAgendamento)}</TableCell>
                        <TableCell align="right" style={{ fontWeight: 700 }}>{rel.totais.vendas}</TableCell>
                        <TableCell align="right" style={{ fontWeight: 700 }}>{fmtMoney(rel.totais.valorVendas)}</TableCell>
                        <TableCell align="right" style={{ fontWeight: 700 }}>{fmtMoney(rel.totais.comissaoVenda)}</TableCell>
                        <TableCell align="right" style={{ fontWeight: 700, color: "#1565c0" }}>{fmtMoney(rel.totais.comissaoTotal)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                </div>
              )}

              {rel.medicos && rel.medicos.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Médicos (repasse)</Typography>
                  <div style={{ overflowX: "auto", width: "100%" }}>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Médico</TableCell><TableCell align="right">Vendas</TableCell><TableCell align="right">Base</TableCell><TableCell align="right">Comissão</TableCell></TableRow></TableHead>
                    <TableBody>
                      {rel.medicos.map((m) => (
                        <TableRow key={m.professionalId} hover>
                          <TableCell>{m.nome}</TableCell>
                          <TableCell align="right">{m.vendas}</TableCell>
                          <TableCell align="right">{fmtMoney(m.valorBase)}</TableCell>
                          <TableCell align="right" style={{ fontWeight: 600 }}>{fmtMoney(m.comissao)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* ---------- DIALOG: meta (faixa nomeada) ---------- */}
      <Dialog open={!!dlg} onClose={saving ? undefined : () => setDlg(null)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        {dlg && (
          <>
            <DialogTitle>{dlg.data.id ? "Editar meta" : "Nova meta (pedra)"}</DialogTitle>
            <DialogContent>
              <TextField label="Nome" placeholder="Ex.: Ouro, Diamante, Terras Raras…" value={dlg.data.name || ""} onChange={(e) => d({ name: e.target.value })} fullWidth margin="dense" />
              <TextField type="number" label="% sobre a base do mês" value={dlg.data.pct ?? ""} onChange={(e) => d({ pct: e.target.value })} fullWidth margin="dense" helperText="Ex.: 20 = meta é a base + 20%." />
              <Box display="flex" alignItems="center" style={{ gap: 12, marginTop: 8 }}>
                <Typography variant="body2">Cor da pedra:</Typography>
                <input type="color" value={dlg.data.gemColor || "#a855f7"} onChange={(e) => d({ gemColor: e.target.value })} style={{ width: 44, height: 30, border: "none", background: "none", cursor: "pointer" }} />
                <TextField type="number" label="Ordem" value={dlg.data.sortOrder ?? ""} onChange={(e) => d({ sortOrder: e.target.value })} margin="dense" style={{ width: 90 }} />
              </Box>
              <FormControlLabel control={<Checkbox checked={dlg.data.active !== false} onChange={(e) => d({ active: e.target.checked })} />} label="Ativa" />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDlg(null)} disabled={saving}>Cancelar</Button>
              <Button variant="contained" color="primary" onClick={saveTier} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null}>Salvar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default MeloPainelTab;
